import React, { useEffect, useRef, useState } from 'react';
import { concluirServicoInacabado, getCar } from '../../../api/carsApi';
import { ServicoInacabado } from '../../../interfaces/AtividadeCarro';
import { useEsteiras } from '../../../hooks/useEsteiras';
import ReleaseStatusInfo from '../../release-status-info/ReleaseStatusInfo';
import ObservacoesAtendimento from '../../observacoes-atendimento/ObservacoesAtendimento';
import ProgressoSubtarefas from '../../progresso-subtarefas/ProgressoSubtarefas';
import EsteirasParalelas from '../../esteiras-paralelas/EsteirasParalelas';
import SeloEsteiraParalela from '../../selo-esteira-paralela/SeloEsteiraParalela';
import AbrirEsteiraParalelaModal from '../abrir-esteira-paralela/AbrirEsteiraParalelaModal';
import ControleParalelasModal from '../controle-paralelas/ControleParalelasModal';
import ComentariosDoVeiculo from '../../comentarios/ComentariosDoVeiculo';
import TagsDaEsteira from '../../tags-da-esteira/TagsDaEsteira';
import SeloPosEvento from '../../selo-pos-evento/SeloPosEvento';
import SeloPrazoDaEtapa from '../../selo-prazo/SeloPrazoDaEtapa';
import { useIsTi } from '../../../hooks/useUserType';
import ConfirmModal from '../confirm/ConfirmModal';
import { notify } from '../../../shared/feedback/notify';

import Pen from '../../../img/pen.svg';
import Mecanico from '../../../img/mecanico.svg';
import Calendar from '../../../img/calendar.svg';
import ChangeStatus from '../../../img/change-status.svg';
import ApproveStatus from '../../../img/approve-status.svg';
import Diagrama from '../../sidebar/img/diagram.svg';
import EyeIcon from '../../../img/eye.svg';
import CarServicesPlanningModal from '../car-services-planning/CarServicesPlanningModal';

import './ChefeDeOficinaActionsModal.css';
import '../../../shared/ui/modal-close-button.css'; // X padrão do sistema

interface ChefeDeOficinaActionsModalProps {
  isOpen: boolean;
  carId: string | null;
  onCancel: () => void;
  onOpenEditModal: (carId: string) => void;
  onOpenCollaboratorModal: (carId: string) => void;
  onOpenUpdateDaysModal: (car: any) => void;
  onOpenStatusModal: (carId: string) => void;
  /**
   * at-89: abre a edição do motivo da pendência da esteira atual. Prop OPCIONAL — o botão só
   * aparece quando o chamador a passa, e ela nunca é passada para o TI (ADR 0012).
   */
  onEditarMotivo?: (carId: string) => void;
  onOpenChecklistHistory: (carId: string, carPlate: string) => void;
  /** Abre o fluxo de aprovação da esteira pendente do veículo. */
  onApproveEsteira?: (carId: string) => void;
  /** Decide se o usuário logado pode aprovar a esteira pendente deste veículo. */
  canApproveEsteira?: (car: any) => boolean;
  /** Item de horas pendentes deste veículo (getPendingSLACars) — habilita "Aprovar Horas". */
  pendingHorasCar?: any | null;
  /** Abre o fluxo de aprovação de horas (mesmo do "Decidir" das notificações). */
  onApproveHoras?: (slaCar: any) => void;
  /** Enquanto true (modal empilhado por cima), o clique fora não fecha este modal. */
  suppressClickOutside?: boolean;
  /** Incrementado pelo pai para recarregar os dados do veículo (ex: após aprovação). */
  refreshKey?: number;
  sidebarRef?: React.RefObject<HTMLDivElement>;
}

const ChefeDeOficinaActionsModal: React.FC<ChefeDeOficinaActionsModalProps> = ({
  isOpen,
  carId,
  onCancel,
  onOpenEditModal,
  onOpenCollaboratorModal,
  onOpenUpdateDaysModal,
  onOpenStatusModal,
  onEditarMotivo,
  onOpenChecklistHistory,
  onApproveEsteira,
  canApproveEsteira,
  pendingHorasCar,
  onApproveHoras,
  suppressClickOutside = false,
  refreshKey = 0,
  sidebarRef,
}) => {
  const { labelDe } = useEsteiras(true);
  // TI vê o modal em modo somente leitura — apenas o Histórico de Checklists fica disponível (at-25).
  const isTi = useIsTi();
  const modalRef = useRef<HTMLDivElement>(null);
  const [car, setCar] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isServicesPlanningOpen, setIsServicesPlanningOpen] = useState(false);
  // at-46: pendências de serviço inacabado e a conclusão manual pelo Chefe.
  const [concluindoEsteiraId, setConcluindoEsteiraId] = useState<string | null>(null);

  const servicosInacabados: ServicoInacabado[] = (car?.servicosInacabados ?? []).filter(
    (s: ServicoInacabado) => s.estado === 'PAUSADA',
  );

  /**
   * at-65 (BUG-V48): era o único `window.confirm` do sistema — diálogo nativo do navegador,
   * fora do padrão de modais do projeto, sem estilo, sem o texto explicativo que as demais
   * confirmações têm (requisito do at-18/at-46: "toda ação nova avisa antes de executar") e
   * bloqueando a aba inteira. Passa a usar o `ConfirmModal` compartilhado.
   */
  const [confirmarConclusao, setConfirmarConclusao] = useState<ServicoInacabado | null>(null);
  // at-99: abertura de esteira em paralelo, e o contador que recarrega o bloco depois dela.
  const [abrirParalelaAberto, setAbrirParalelaAberto] = useState(false);
  const [recarregarParalelas, setRecarregarParalelas] = useState(0);
  // at-99: o selo ao lado da placa abre o controle das paralelas (pedido do Victor, 10/09/2026).
  const [controleParalelasAberto, setControleParalelasAberto] = useState(false);

  const nomeDaEsteira = (servico: ServicoInacabado) =>
    servico.esteiraNome || labelDe(servico.esteiraId) || 'esta esteira';

  const handleConcluirServico = async (servico: ServicoInacabado) => {
    if (!car?.id) return;
    const nome = nomeDaEsteira(servico);

    setConcluindoEsteiraId(servico.esteiraId);
    try {
      await concluirServicoInacabado(car.id, servico.esteiraId);
      notify.success(`O serviço de ${nome} foi marcado como concluído.`);
      await fetchCarData(car.id);
    } catch (err: any) {
      notify.actionError(err, { action: 'complete', subject: 'o serviço' });
    } finally {
      setConcluindoEsteiraId(null);
      setConfirmarConclusao(null);
    }
  };

  const fetchCarData = async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const carData = await getCar(id);
      setCar(carData);
    } catch (err) {
      setError('Erro ao carregar os dados do veículo.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && carId) {
      fetchCarData(carId);
    }
  }, [isOpen, carId, refreshKey]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      /**
       * ⚠️ Todo modal FILHO aberto sobre este precisa entrar nesta guarda.
       *
       * O filho é renderizado FORA do `ref` deste modal, então qualquer clique dentro dele é
       * contado como "clique fora" aqui — e o hospedeiro se fecha no primeiro clique,
       * derrubando o filho junto. É a armadilha registrada em `03-code-conventions.md`, que já
       * mordeu o `ChecklistModal` (drawer "Como Funciona") e o `RecepcaoActionsModal` (cadastro
       * rápido de contato), e mordeu de novo na at-99 com o "Abrir esteira em paralelo".
       */
      if (
        isServicesPlanningOpen ||
        suppressClickOutside ||
        abrirParalelaAberto ||
        controleParalelasAberto
      ) {
        return;
      }

      const clickedNode = event.target as Node;
      const isOutsideModal = modalRef.current && !modalRef.current.contains(clickedNode);
      const isOutsideSidebar = sidebarRef?.current && !sidebarRef.current.contains(clickedNode);

      if (isOutsideModal && isOutsideSidebar) {
        onCancel();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [
    isOpen,
    onCancel,
    sidebarRef,
    isServicesPlanningOpen,
    suppressClickOutside,
    abrirParalelaAberto,
    controleParalelasAberto,
  ]);

  if (!isOpen) return null;

  /**
   * at-99: o botão "Abrir Esteira em Paralelo" só aparece quando a etapa atual é de TRABALHO.
   *
   * Em etapa de fluxo livre (Aguardando, Orçamento, Sindicância…) o backend recusa com
   * `PRINCIPAL_NAO_ACEITA_PARALELA` — decisão do Victor de 10/09/2026: enquanto a etapa não é
   * trabalho, o veículo não está sendo trabalhado. Oferecer o botão ali levaria a um erro
   * previsível, que é justamente o que a regra do ADR 0012 evita para o TI.
   */
  const podeAbrirParalela = car?.esteira?.sistema === false;

  const formatDate = (date?: string | null) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('pt-BR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: 'UTC',
    });
  };

  return (
    <div className="add-car-modal-overlay">
      <div className="chefe-oficina-actions-modal-content" ref={modalRef}>
        
        {/* Modal Header */}
        <div className="chefe-oficina-actions-modal-header">
          <div className="header-info">
            <h2>
              {car?.plate || 'Carregando...'}
              {/* at-89: motivo da pendência ao lado da placa (Victor, 03/09/2026). */}
              {car?.id && (
                <TagsDaEsteira carId={car.id} esteiraAtual={car.status} variante="inline" />
              )}
              {/* at-96: retorno de garantia. Um pós-evento indistinguível de serviço novo faz a
                  oficina tratar retrabalho como produção. */}
              <SeloPosEvento posEvento={car?.posEvento} />
              <SeloPrazoDaEtapa car={car} />
              {/* at-99: há serviço em curso em outra esteira ao mesmo tempo. Sem o selo, quem
                  olha a lista vê o veículo parado numa etapa e não sabe que outro setor está
                  trabalhando nele — a invisibilidade que a oficina resolvia movendo o carro. */}
              <SeloEsteiraParalela
                esteirasParalelas={car?.esteirasParalelas}
                onClick={() => setControleParalelasAberto(true)}
              />
            </h2>
            {car?.model && <span className="vehicle-model">{car.model}</span>}
          </div>
          <button className="close-button" onClick={onCancel} aria-label="Fechar modal">
            &times;
          </button>
        </div>

        {loading ? (
          <div className="chefe-oficina-modal-loading">Carregando dados do veículo...</div>
        ) : error ? (
          <div className="chefe-oficina-modal-error">{error}</div>
        ) : car ? (
          <div className="chefe-oficina-actions-modal-body">
            
            {/* Left Section: Details & Images */}
            <div className="chefe-oficina-details-left">
              
              {/* Info Grid */}
              <div className="info-section">
                <h3>Informações do Veículo</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="info-label">Nº do Evento</span>
                    <span className="info-value">{car.eventNumber || '-'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Chassi</span>
                    <span className="info-value">{car.chassis || '-'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Classe</span>
                    <span className="info-value">{car.carClass || '-'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Tipo de Dano</span>
                    <span className="info-value">{car.damageType || '-'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Cor</span>
                    <span className="info-value">{car.color || '-'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Data de Entrada</span>
                    <span className="info-value">{formatDate(car.entryDate)}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Dias na Oficina</span>
                    <span className="info-value">{car.daysInShop ?? '-'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Esteira Atual</span>
                    <span className="info-value status-badge">{car.status ? labelDe(car.status) : 'Fazer Checklist'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Esteira Pendente</span>
                    <span className="info-value pending-badge">{labelDe(car.pendingStatus)}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Colaboradores</span>
                    <span className="info-value">{car.responsibleName || car.responsible?.name || '-'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Data de Aprovação</span>
                    <span className="info-value">{formatDate(car.authorizedAt)}</span>
                  </div>
                  <ReleaseStatusInfo car={car} />
                </div>

                {/* at-54: texto livre do atendimento (componente compartilhado; some quando vazio) */}
                <ObservacoesAtendimento
                  observacoesCliente={car.observacoesCliente}
                  observacoesOficina={car.observacoesOficina}
                />

                {/* at-99: esteiras com serviço em curso além da etapa (some quando não há) */}
                <EsteirasParalelas
                  carId={car.id}
                  podeEncerrar={!isTi}
                  recarregar={recarregarParalelas}
                  onEncerrada={() => void fetchCarData(car.id)}
                />

                {/* at-89: o que já foi feito, por esteira (some quando não há sub-tarefas) */}
                <ProgressoSubtarefas carId={car.id} />
                <ComentariosDoVeiculo carId={car.id} />

                {/* at-46: pendência de serviço inacabado — o que ficou por terminar, com quem
                    parou, quando e por quê. O Chefe pode encerrar a pendência aqui quando o
                    serviço não vai ser retomado. */}
                {servicosInacabados.length > 0 && (
                  <div className="servico-inacabado-bloco">
                    <h4>Serviço inacabado</h4>
                    {servicosInacabados.map((servico) => (
                      <div key={servico.esteiraId} className="servico-inacabado-item">
                        <div className="servico-inacabado-texto">
                          <strong>{servico.esteiraNome || labelDe(servico.esteiraId)}</strong>
                          {servico.pausadoEm ? ` — parado em ${formatDate(servico.pausadoEm)}` : ''}
                          {servico.pausadoPorNome ? ` por ${servico.pausadoPorNome}` : ''}
                          {/* at-134: motivo do catálogo + o detalhe; pausas antigas só têm o texto. */}
                          {servico.motivoParada
                            ? `. Motivo: ${servico.motivoParada.nome}${servico.observacao ? ` (${servico.observacao})` : ''}`
                            : servico.observacao
                              ? `. Motivo: ${servico.observacao}`
                              : ''}
                        </div>
                        {!isTi && (
                          <button
                            type="button"
                            className="servico-inacabado-concluir"
                            disabled={concluindoEsteiraId === servico.esteiraId}
                            onClick={() => setConfirmarConclusao(servico)}
                          >
                            {concluindoEsteiraId === servico.esteiraId
                              ? 'Concluindo...'
                              : 'Marcar como concluído'}
                          </button>
                        )}
                      </div>
                    ))}
                    <p className="servico-inacabado-hint">
                      O serviço volta a ser trabalhado quando o veículo retornar para esta esteira.
                      Marcar como concluído encerra a pendência sem retomada.
                    </p>
                  </div>
                )}
              </div>

            </div>

            {/* Right Section: Actions Sidebar */}
            <div className="chefe-oficina-actions-right">
              <h3>Ações do Chefe de Oficina</h3>
              
              <div className="actions-list">

                {!isTi && (
                <button
                  type="button"
                  className="action-button-item"
                  onClick={() => setIsServicesPlanningOpen(true)}
                >
                  <img
                    src={Diagrama}
                    alt=""
                    className="action-icon"
                    style={{
                      filter: 'brightness(0) saturate(100%) invert(43%) sepia(96%) saturate(1995%) hue-rotate(357deg) brightness(94%) contrast(96%)',
                    }}
                  />
                  <span>Serviços a Realizar</span>
                </button>
                )}

                {!isTi && (
                <button
                  type="button"
                  className="action-button-item"
                  onClick={() => onOpenEditModal(car.id)}
                >
                  <img src={Pen} alt="" className="action-icon" />
                  <span>Editar Veículo</span>
                </button>
                )}

                {!isTi && (
                <button
                  type="button"
                  className="action-button-item"
                  onClick={() => onOpenCollaboratorModal(car.id)}
                >
                  <img src={Mecanico} alt="" className="action-icon" />
                  <span>Definir Colaboradores</span>
                </button>
                )}

                {!isTi && (
                <button
                  type="button"
                  className="action-button-item"
                  onClick={() => onOpenUpdateDaysModal(car)}
                >
                  <img src={Calendar} alt="" className="action-icon" />
                  <span>Atualizar Previsão</span>
                </button>
                )}

                {!isTi && (
                <button
                  type="button"
                  className="action-button-item"
                  onClick={() => onOpenStatusModal(car.id)}
                >
                  <img src={ChangeStatus} alt="" className="action-icon" />
                  <span>Alterar Esteira</span>
                </button>
                )}

                {/* at-89: ação própria para o motivo da pendência. Antes só existia dentro do
                    "Editar Veículo", e por isso passava batido. `!isTi` como as demais: é
                    escrita, e o TI não age (ADR 0012). */}
                {!isTi && onEditarMotivo && (
                  <button
                    type="button"
                    className="action-button-item"
                    onClick={() => onEditarMotivo(car.id)}
                  >
                    <img src={ChangeStatus} alt="" className="action-icon" />
                    <span>Atualizar Motivo da Pendência</span>
                  </button>
                )}

                {/* at-99 (ADR 0050): abrir esteira em paralelo. É o que substitui o vai-e-volta
                    de esteira para "emprestar" o veículo a outro ofício. `!isTi` como as demais
                    (ADR 0012), e só quando a etapa atual é de TRABALHO — em etapa de fluxo livre
                    o backend recusa, e oferecer o botão levaria a um erro previsível. */}
                {!isTi && car.esteira && car.esteira.chave && podeAbrirParalela && (
                  <button
                    type="button"
                    className="action-button-item"
                    onClick={() => setAbrirParalelaAberto(true)}
                  >
                    <img src={ChangeStatus} alt="" className="action-icon" />
                    <span>Abrir Esteira em Paralelo</span>
                  </button>
                )}

                {!isTi && Boolean(car.pendingStatus) && onApproveEsteira && canApproveEsteira?.(car) && (
                  <button
                    type="button"
                    className="action-button-item"
                    onClick={() => onApproveEsteira(car.id)}
                  >
                    <img src={ApproveStatus} alt="" className="action-icon" />
                    <span>Aprovar Esteira</span>
                  </button>
                )}

                {!isTi && pendingHorasCar && onApproveHoras && (
                  <button
                    type="button"
                    className="action-button-item"
                    onClick={() => onApproveHoras(pendingHorasCar)}
                  >
                    <img src={ApproveStatus} alt="" className="action-icon" />
                    <span>
                      Aprovar Horas
                      {pendingHorasCar.pendingSessionsCount
                        ? ` (${pendingHorasCar.pendingSessionsCount} sessão${pendingHorasCar.pendingSessionsCount !== 1 ? 'ões' : ''} pendente${pendingHorasCar.pendingSessionsCount !== 1 ? 's' : ''})`
                        : ''}
                    </span>
                  </button>
                )}

                <button
                  type="button"
                  className="action-button-item"
                  onClick={() => onOpenChecklistHistory(car.id, car.plate)}
                >
                  <img src={EyeIcon} alt="" className="action-icon" />
                  <span>Histórico de Checklists</span>
                </button>

              </div>
            </div>

          </div>
        ) : (
          <div className="chefe-oficina-modal-error">Nenhum dado encontrado.</div>
        )}

      </div>

      <CarServicesPlanningModal
        isOpen={isServicesPlanningOpen}
        carId={carId}
        carPlate={car?.plate}
        carModel={car?.model}
        onCancel={() => setIsServicesPlanningOpen(false)}
        sidebarRef={sidebarRef}
      />

      {/* at-65 (BUG-V48): substitui o `window.confirm` nativo pelo padrão do sistema. */}
      <ConfirmModal
        isOpen={Boolean(confirmarConclusao)}
        title="Concluir serviço sem retomada"
        message={
          confirmarConclusao
            ? `O serviço de ${nomeDaEsteira(confirmarConclusao)} será dado como concluído sem retomada: ` +
              'o veículo deixa de ter essa pendência e ela não poderá ser retomada de onde parou. ' +
              'A ação fica registrada na Auditoria.'
            : ''
        }
        confirmLabel="Concluir serviço"
        onConfirm={() => {
          if (confirmarConclusao) void handleConcluirServico(confirmarConclusao);
        }}
        onCancel={() => setConfirmarConclusao(null)}
      />

      {/* at-99: abre empilhado sobre este modal. ⚠️ O modal filho é renderizado FORA do `ref`
          deste, então o handler de "clicou fora" daqui contaria o clique nele como externo —
          é por isso que o `suppressClickOutside` existe, e por isso o filho é montado
          condicionalmente em vez de ficar sempre montado com `isOpen`. */}
      {abrirParalelaAberto && car && (
        <AbrirEsteiraParalelaModal
          carId={car.id}
          plate={car.plate}
          esteiraPrincipalId={car.esteira?.id ?? car.esteiraId}
          esteiraPrincipalNome={car.esteira?.nome ?? labelDe(car.status)}
          jaAbertasIds={(car.esteirasParalelas ?? []).map((p: any) => p.esteiraId)}
          esteirasLiberadasIds={(car.esteirasLiberadas ?? []).map((l: any) => l.esteiraId)}
          liberacaoTotal={Boolean(car.releasedAt)}
          onCancel={() => setAbrirParalelaAberto(false)}
          onAberta={() => {
            setRecarregarParalelas((valor) => valor + 1);
            void fetchCarData(car.id);
          }}
          sidebarRef={sidebarRef}
        />
      )}

      {/* at-99: o controle aberto pelo selo. Mesma armadilha do modal acima — está na guarda de
          "clicou fora" deste modal. */}
      {controleParalelasAberto && car && (
        <ControleParalelasModal
          carId={car.id}
          plate={car.plate}
          esteiraPrincipalId={car.esteira?.id ?? car.esteiraId}
          esteiraPrincipalNome={car.esteira?.nome ?? labelDe(car.status)}
          principalEhDeTrabalho={car.esteira ? car.esteira.sistema === false : undefined}
          esteirasParalelas={car.esteirasParalelas}
          esteirasLiberadasIds={(car.esteirasLiberadas ?? []).map((l: any) => l.esteiraId)}
          liberacaoTotal={Boolean(car.releasedAt)}
          somenteLeitura={isTi}
          onCancel={() => setControleParalelasAberto(false)}
          onAlterado={() => {
            setRecarregarParalelas((valor) => valor + 1);
            void fetchCarData(car.id);
          }}
          sidebarRef={sidebarRef}
        />
      )}
    </div>
  );
};

export default ChefeDeOficinaActionsModal;
